import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  GroupAdd as JoinIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  Dashboard as BoardsIcon,
  AccountBalance as BalancesIcon,
  AccountBalanceWallet as AccountingIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  HelpOutline as HelpIcon,
  Article as BlogIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, usePageTracking, useNotifications } from '../hooks';
import logoSvg from '../assets/logo.svg';
import kLogoSvg from '../assets/k-logo.svg';
import { boardsApi } from '../api';
import { isAdmin } from '../types';
import { PlayerAvatar } from './shared/PlayerAvatar';
import { CreditBalance } from './CreditBalance';
import { TosAcceptanceModal } from './shared/TosAcceptanceModal';
import { NotificationBell } from './shared/NotificationBell';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { unreadCount } = useNotifications(); // Just for mobile drawer badge
  const theme = useTheme();

  // Track page views on route changes
  usePageTracking();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Navigation helper that supports ctrl/cmd+click for new tab
  const handleNavClick = (e: React.MouseEvent, path: string, closeMobileMenu = false) => {
    // Allow ctrl/cmd+click or middle-click to open in new tab
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return; // Let the browser handle it naturally via href
    }
    e.preventDefault();
    navigate(path);
    if (closeMobileMenu) handleMobileMenuClose();
  };


  const handleOpenJoinDialog = () => {
    setJoinDialogOpen(true);
    setInviteCode('');
    setJoinError(null);
  };

  const handleCloseJoinDialog = () => {
    setJoinDialogOpen(false);
    setInviteCode('');
    setJoinError(null);
  };

  const handleJoinBoard = async () => {
    if (!inviteCode.trim()) {
      setJoinError('Please enter an invite code');
      return;
    }

    setJoining(true);
    setJoinError(null);

    try {
      const board = await boardsApi.join(inviteCode.trim());
      handleCloseJoinDialog();
      navigate(`/boards/${board.slug || board.id}`);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setJoinError(error.message || 'Failed to join board. Please check the invite code.');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !joining && inviteCode.trim()) {
      handleJoinBoard();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          {/* Mobile hamburger menu button */}
          {isAuthenticated && isMobile && (
            <IconButton
              color="inherit"
              aria-label="open navigation menu"
              edge="start"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component="a"
            href="/"
            onClick={(e: React.MouseEvent) => handleNavClick(e, '/')}
            sx={{ display: 'flex', alignItems: 'center', mr: 2 }}
          >
            <Box
              component="img"
              src={logoSvg}
              alt="SquareUp"
              sx={{
                height: 32,
                cursor: 'pointer',
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {isAuthenticated ? (
            <>
              {/* Desktop navigation buttons */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Button component="a" href="/boards" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/boards')}>
                  My Boards
                </Button>
                <Button component="a" href="/balances" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/balances')}>
                  My Balances
                </Button>
                <Button component="a" href="/accounting" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/accounting')}>
                  Accounting
                </Button>
                <Tooltip title="Theme Store">
                  <IconButton component="a" href="/themes" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/themes')}>
                    <StoreIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Blog">
                  <IconButton component="a" href="/blog" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/blog')}>
                    <BlogIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Support Center">
                  <IconButton component="a" href="/support" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/support')}>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
              </Box>
              <Box sx={{ mr: 1 }}>
                <CreditBalance variant="chip" onClick={() => navigate('/credits')} lightMode />
              </Box>
              {/* Notification bell - shown on all screen sizes for authenticated users */}
              <NotificationBell lightMode />
              {/* Desktop action buttons */}
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Tooltip title="Join Board">
                  <IconButton color="inherit" onClick={handleOpenJoinDialog}>
                    <JoinIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create Board">
                  <IconButton component="a" href="/create-board" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/create-board')}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                {isAdmin(user) && (
                  <Tooltip title="Admin Dashboard">
                    <IconButton component="a" href="/admin" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/admin')}>
                      <AdminIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Tooltip title="Profile Settings">
                <IconButton
                  component="a"
                  href="/profile"
                  size="large"
                  aria-label="profile settings"
                  onClick={(e: React.MouseEvent) => handleNavClick(e, '/profile')}
                  color="inherit"
                >
                  <PlayerAvatar
                    displayName={user?.displayName || ''}
                    handle={user?.handle}
                    iconName={user?.iconName}
                    size="medium"
                    showBadge={false}
                    disableTooltip
                  />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
                <Tooltip title="Blog">
                  <IconButton component="a" href="/blog" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/blog')}>
                    <BlogIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Support Center">
                  <IconButton component="a" href="/support" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/support')}>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Button component="a" href="/login" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/login')}>
                Login
              </Button>
              <Button component="a" href="/register" color="inherit" onClick={(e: React.MouseEvent) => handleNavClick(e, '/register')}>
                Register
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'primary.main',
              color: 'white',
            }}
          >
            <Box
              component="img"
              src={logoSvg}
              alt="SquareUp"
              sx={{ height: 28 }}
            />
            <IconButton onClick={handleMobileMenuClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ flex: 1, py: 1 }}>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/boards" onClick={(e: React.MouseEvent) => handleNavClick(e, '/boards', true)}>
                <ListItemIcon>
                  <BoardsIcon />
                </ListItemIcon>
                <ListItemText primary="My Boards" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/balances" onClick={(e: React.MouseEvent) => handleNavClick(e, '/balances', true)}>
                <ListItemIcon>
                  <BalancesIcon />
                </ListItemIcon>
                <ListItemText primary="My Balances" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/accounting" onClick={(e: React.MouseEvent) => handleNavClick(e, '/accounting', true)}>
                <ListItemIcon>
                  <AccountingIcon />
                </ListItemIcon>
                <ListItemText primary="Accounting" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/notifications" onClick={(e: React.MouseEvent) => handleNavClick(e, '/notifications', true)}>
                <ListItemIcon>
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationsIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary="Notifications" />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleMobileMenuClose(); handleOpenJoinDialog(); }}>
                <ListItemIcon>
                  <JoinIcon />
                </ListItemIcon>
                <ListItemText primary="Join Board" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/create-board" onClick={(e: React.MouseEvent) => handleNavClick(e, '/create-board', true)}>
                <ListItemIcon>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText primary="Create Board" />
              </ListItemButton>
            </ListItem>
            {isAdmin(user) && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem disablePadding>
                  <ListItemButton component="a" href="/admin" onClick={(e: React.MouseEvent) => handleNavClick(e, '/admin', true)}>
                    <ListItemIcon>
                      <AdminIcon />
                    </ListItemIcon>
                    <ListItemText primary="Admin Dashboard" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton component="a" href="/blog" onClick={(e: React.MouseEvent) => handleNavClick(e, '/blog', true)}>
                <ListItemIcon>
                  <BlogIcon />
                </ListItemIcon>
                <ListItemText primary="Blog" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component="a" href="/support" onClick={(e: React.MouseEvent) => handleNavClick(e, '/support', true)}>
                <ListItemIcon>
                  <HelpIcon />
                </ListItemIcon>
                <ListItemText primary="Support Center" />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Navigation Menu
            </Typography>
          </Box>
        </Box>
      </Drawer>

      <Container component="main" sx={{ flex: 1, py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={logoSvg}
            alt="SquareUp"
            sx={{
              height: 20,
              opacity: 0.7,
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
            by
          </Typography>
          <Box
            component="a"
            href="https://katapultmedia.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={kLogoSvg}
              alt="Katapult"
              sx={{
                height: 20,
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.5 }}>|</Typography>
        <Button
          size="small"
          startIcon={<BlogIcon />}
          onClick={() => navigate('/blog')}
          sx={{
            color: 'text.secondary',
            opacity: 0.7,
            '&:hover': { opacity: 1 },
            textTransform: 'none',
          }}
        >
          Blog
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.5 }}>|</Typography>
        <Button
          size="small"
          startIcon={<HelpIcon />}
          onClick={() => navigate('/support')}
          sx={{
            color: 'text.secondary',
            opacity: 0.7,
            '&:hover': { opacity: 1 },
            textTransform: 'none',
          }}
        >
          Support
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.5 }}>|</Typography>
        <Button
          size="small"
          onClick={() => navigate('/terms')}
          sx={{
            color: 'text.secondary',
            opacity: 0.7,
            '&:hover': { opacity: 1 },
            textTransform: 'none',
          }}
        >
          Terms
        </Button>
        <Button
          size="small"
          onClick={() => navigate('/privacy')}
          sx={{
            color: 'text.secondary',
            opacity: 0.7,
            '&:hover': { opacity: 1 },
            textTransform: 'none',
          }}
        >
          Privacy
        </Button>
      </Box>

      {/* ToS Acceptance Modal - blocks authenticated users without ToS acceptance */}
      {!isLoading && isAuthenticated && user && !user.tosAcceptedAt && (
        <TosAcceptanceModal open={true} />
      )}

      {/* Join Board Dialog */}
      <Dialog open={joinDialogOpen} onClose={handleCloseJoinDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Join a Board</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Join a friend's squares board to participate in their game. The board manager will share an invite code with you — ask them for it if you don't have one yet.
          </Typography>
          {joinError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {joinError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Invite Code"
            placeholder="e.g., ABC123XY"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            onKeyPress={handleJoinKeyPress}
            disabled={joining}
            helperText="The 8-character code shared by the board manager"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJoinDialog} disabled={joining}>
            Cancel
          </Button>
          <Button
            onClick={handleJoinBoard}
            variant="contained"
            disabled={joining || !inviteCode.trim()}
          >
            {joining ? 'Joining...' : 'Join Board'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
